import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { DURATION_FAST } from '../utils/animationTiming';
import { LAYER_COLOR_CLASSES, WISHLIST_LEGEND_CLASSES } from '../constants/layerColors';

interface LegendItem {
  label: string;
  colorClass: string;
}

// Colors match actual marker CSS in index.css - uses shared constants
const LEGEND_ITEMS: LegendItem[] = [
  { label: 'Travels', colorClass: LAYER_COLOR_CLASSES.travels },
  { label: 'Love is Blind', colorClass: LAYER_COLOR_CLASSES['love-is-blind'] },
  { label: 'Wishlist', colorClass: WISHLIST_LEGEND_CLASSES },
];

interface MapLegendProps {
  isTimeMachineMode?: boolean;
}

export function MapLegend({ isTimeMachineMode = false }: MapLegendProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  // Move legend up when timeline control is visible
  const bottomClass = isTimeMachineMode
    ? 'bottom-40 lg:bottom-28' // Above timeline control
    : 'bottom-20 lg:bottom-8';

  return (
    <div
      className={`absolute ${bottomClass} left-4 z-[1000] transition-[bottom] duration-200`}
      data-testid="map-legend"
    >
      <div className="bg-bg-surface/90 backdrop-blur-sm rounded-lg border border-gray-700 shadow-lg overflow-hidden">
        {/* Header - always visible */}
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-3 py-2 flex items-center justify-between gap-2 text-sm text-text-secondary hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-inset"
          aria-expanded={isExpanded}
          aria-controls="legend-content"
        >
          <span className="font-medium">Legend</span>
          <motion.svg
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: DURATION_FAST / 1000 }}
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M6 9l6 6 6-6" />
          </motion.svg>
        </button>

        {/* Content - collapsible */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              id="legend-content"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: DURATION_FAST / 1000 }}
              className="overflow-hidden"
            >
              <div className="px-3 pb-3 space-y-2">
                {LEGEND_ITEMS.map((item) => (
                  <div
                    key={item.label}
                    className="flex items-center gap-2"
                  >
                    <div
                      className={`w-3 h-3 rounded-full ${item.colorClass} shrink-0`}
                      aria-hidden="true"
                    />
                    <span className="text-xs text-text-secondary">
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
